import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Product } from '@app/modules/model/product';
import { ProductSelect } from '@app/modules/model/productselect';
import { Place } from '@app/modules/model/place';
import { QrCode } from '@app/modules/model/qrcode';

import { GenericUtils } from '@app/app.commonutils';
import { languageSupport, languageService } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';

import { UiAmountModal } from '@app/components/model/product/ui-count/ui-count';

@Component({
    selector: 'ui-modal-searchno',
    templateUrl:'./ui-searchno.html',
    styleUrls: [ 
        './ui-searchno.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSearchNumberModal extends languageSupport implements OnInit, OnDestroy, AfterViewInit {
    public _hidden: boolean = false;
    public _place: Place = null;
    public _target: String = null;  // 'prd' | 'tbl'

    normalize(str){
        return GenericUtils.Normalize(str);
    }

    /* match products template */
    public _products: Array<Product | ProductSelect> = [];

    private _productcode = null;
    get ProductCode(){
        if ((this._productcode == null) || (this._productcode == '')){
            return null;
        }

        return this._productcode;
    }

    set ProductCode(value: string){
        let _productcode = (value === '' || value == null) ? null : value;
        if (_productcode != this._productcode){
            this._productcode = _productcode;
            this._ProductSearch(this._productcode);
        }
    }
   
    get MatchProducts(){
        return this._products;
    }

    set MatchProducts(value){
        this._products = value;

        this.change.markForCheck();
        setTimeout(() => {
            this.OnScroll();
        }, 0);
    }  

    /* match qrcodes template */
    public _qrcodes: Array<QrCode>

    private _qrStatus(_qrcode){
        if (_qrcode.status == 'PA'){
            return 'wait';
        }

        if (_qrcode.Attention){
            return 'goto';
        }

        return (_qrcode.IsBusy) ? 'busy' : 'free';
    }

    private _toColor(color){
        if (this.view.LightTheme){
            if (color == 'medium'){
                return 'dark';
            }
        }

        return color;
    }

    QrColor(_qrcode){
        switch(this._qrStatus(_qrcode)){
            case 'free': return this._toColor('medium');
            case 'busy': return this._toColor('success');
            case 'goto': return this._toColor('warning');
        }
        return this._toColor('medium');    
    }

    QrIcon(_qrcode){
        switch(this._qrStatus(_qrcode)){
            case 'free': return 'scan-circle';
            case 'busy': return 'people-circle';
            case 'goto': return 'hand-right-outline';
        }
        return 'scan-circle';            
    }

    QrStatus(_qrcode){
        return '@qrcode_number_search_status_' + this._qrStatus(_qrcode);
    }

    private _qrcodenumber = null;
    get QrCodeNumber(){
        if ((this._qrcodenumber == null) || (this._qrcodenumber == '')){
            return null;
        }

        return this._qrcodenumber;
    }

    set QrCodeNumber(value: string){
        let _qrcodenumber = (isNaN(parseFloat(value))) ? null : value;
        if (_qrcodenumber != this._qrcodenumber){
            this._qrcodenumber = _qrcodenumber;
            this._QrCodeSearch(this._qrcodenumber);
        }
    }

    get MatchQrCodes(){
        return this._qrcodes;
    }

    set MatchQrCodes(value){
        this._qrcodes = value;

        this.change.markForCheck();
        setTimeout(() => {
            this.OnScroll();
        }, 0);
    }

    /************************/
    /* STARTS HERE          */
    /************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams, public view: viewService) {
        super(lang, change);
    }

    ngOnInit() {
        this._place = this.params.get('place');
        this._target = this.params.get('target');
        // Optional `code` param — when the modal is opened from the barcode
        // scanner with several matching products we pre-fill the search input
        // so the user lands on the filtered short-list directly.
        const _code = this.params.get('code');
        if (_code){
            this.ProductCode = _code;
        }
    }

    ngAfterViewInit(){
        if (this._input && this._input.SetFocus){
            this._input.SetFocus();
        }
    }

    ngOnDestroy(){
        super.OnDestroy();
    }

    OnInputEnter(){
        if (this.ExactMatch){
            this.OnReturn();
        }
    }
    
    /************************/
    /* ITEMS SEARCH        */
    /************************/
    
    private _exactmatch = null;
    get ExactMatch(){
        return this._exactmatch;
    }

    private _ProductSearch(_pattern){
        let _products = [];

        let _exactmatch = [];
        if (_pattern){
            for (let _product of this._place.products){
                if (!_product.IsValid || !_product.IsPurchased){
                    continue;           // product not valid for ticket
                }

                if (_product.code && _product.code.startsWith(_pattern)){
                    _products.push(_product);
                    if (_product.code == _pattern.trim()){
                        _exactmatch.push(_product);
                    }
                }

                for (let _direct of _product.directs){
                    if (!_direct.IsValid || !_direct.IsAvailable){
                        continue;       // direct not valid for ticket
                    }

                    if (_direct.code && _direct.code.startsWith(_pattern)){
                        _products.push(_direct);
                        if (_direct.code == _pattern.trim()){
                            _exactmatch.push(_direct);
                        }
                    }
                }
            }
        }

        this._exactmatch = (_exactmatch.length == 1) ? _exactmatch[0] : null;
        this.MatchProducts = _products.sort((a, b) => {
            return (a.code == b.code) ? 0 : ((a.code < b.code) ? -1 : 1);
        });
    }

    private _QrCodeSearch(_pattern){
        let _qrcodes = [];

        let _exactmatch = [];
        if (_pattern){
            for (let _qrcode of this._place.tables){
                if (!_qrcode.IsValid){
                    continue;           // invalid table
                }

                if (_qrcode.number.toString().startsWith(_pattern)){
                    _qrcodes.push(_qrcode);
                    if (_qrcode.number.toString() == _pattern.trim()){
                        _exactmatch.push(_qrcode);
                    }
                }
            }
        }

        this._exactmatch = (_exactmatch.length == 1) ? _exactmatch[0] : null;
        this.MatchQrCodes = _qrcodes.sort((a, b) => {
            return a.number - b.number;
        });
    }

    /****************************/
    /* PRODUCT EVENT HANDLERS   */
    /****************************/
    
    private _pressed_timeout = null;
    private _ignore_selected = false;

    OnAmount(product){
        if (product.MarketPrice || product.IsWeighted){
            return;     // do not show the amount modal on this products
        }

        return new Promise(async (resolve) => {
            const modal = await this.modalCtrl.create({
                component: UiAmountModal,
                cssClass: 'modal-amount',
                backdropDismiss: false
            });

            modal.onDidDismiss().then((detail) => {
                resolve((detail.data) ? detail.data.amount : null);
            });

            await modal.present();
        });
    }

    private HideModal(value){
        this._hidden = value;
        this.change.markForCheck();
    }

    async OnPressed(down, _product){
        if ((this._target != 'prd') || _product.IsWeighted){
            return;     // only non-weighted products
        }

        if (down){
            if (this._pressed_timeout){
                clearTimeout(this._pressed_timeout);
                this._pressed_timeout = null;
            }

            this._pressed_timeout = setTimeout(async () => {
                this.HideModal(true);        // hide the current modal

                let _amount = await this.OnAmount(_product);
                if (_amount) {
                    await this.modalCtrl.dismiss({ product: _product, amount: _amount });
                }
                else {
                    this.HideModal(false);  // show the current modal
                }

                this._ignore_selected = true;
                setTimeout(() => {  // only ignore on current loop
                    this._ignore_selected = false;
                }, 0);
            }, 1000)
        }
        else {
            if (this._pressed_timeout){
                clearTimeout(this._pressed_timeout);
                this._pressed_timeout = null;
            }
        }
    }   

    async OnSelectProduct(_product){
        if (!this._ignore_selected){
            await this.modalCtrl.dismiss({ product: _product, amount: 1 });
        }
        this._ignore_selected = false;
    }

    /****************************/
    /* QRCODE EVENT HANDLERS    */
    /****************************/

    async OnSelectQrCode(_qrcode){
        await this.modalCtrl.dismiss({ qrcode: _qrcode });
    }

    /****************************/
    /* COMMON EVENT HANDLERS    */
    /****************************/

    @ViewChild('scrbox', {static: false}) _scrbox: ElementRef;    
    @ViewChild('scrlst', {static: false}) _scrlst: ElementRef;    
    OnScroll() {
        let _d = this._scrlst.nativeElement;
        let isTop = (_d.scrollTop === 0);
        let isBot = (_d.scrollHeight - _d.scrollTop === _d.offsetHeight);

        let _t = this._scrbox.nativeElement;
        (isTop) ? _t.classList.add('top') : _t.classList.remove('top');
        (isBot) ? _t.classList.add('bot') : _t.classList.remove('bot');
    }

    async CloseModal() {
        await this.modalCtrl.dismiss(null);
    }

    @ViewChild('input', {static: false}) _input: any;    
    OnNumPad(key){
        if (this._input){
            this._input.OnKeyPress(key);
        }
    }

    OnReturn(){
        if (this._target == 'prd'){
            this.OnSelectProduct(this.ExactMatch);
        }

        if (this._target == 'tbl'){
            this.OnSelectQrCode(this.ExactMatch);            
        }
    }
}